import type { PagePointer } from 'models'
import {
    composedAncestors,
    describe,
    filteredText,
    isExcluded,
    isHidden,
    nameOf,
    roleOf,
    snapshot,
    type PageRefs,
    type PageRules,
} from './snapshot'

/**
 * What the user points at on the page (spec `page-point-and-choose.md`, P):
 * the element a gesture stands for, and how the model reads it, with the refs
 * of the page tools.
 */

/** The part of the page around a pointed element the model reads, at most. */
const AROUND_MAX_CHARS = 2_000
/** A selected text, at most. */
const SELECTED_MAX_CHARS = 1_000
/** What the chip shows, at most. */
const LABEL_MAX_CHARS = 60
/** The shortest selection worth asking about. */
const SELECTED_MIN_CHARS = 2

/** What a gesture can stand for by itself: a control, a link, a heading, an image or a block of text. */
const POINTABLE =
    'a[href], button, input, select, textarea, summary, [role], [contenteditable=""], [contenteditable=true], h1, h2, h3, h4, h5, h6, p, li, dt, dd, td, th, label, blockquote, figcaption, pre, img[alt], legend, caption'
/** Roles that say nothing of what the element is. */
const NO_ROLE = new Set(['presentation', 'none', 'generic'])
/** Containers too large to stand for what the user pointed at. */
const TOO_WIDE = 'html, body, main, [role=main]'
/** The widget's own elements: what lies under them is the page. */
const WIDGET_TAGS = new Set(['EMBEDDED-CHAT-WIDGET', 'FLOATING-CHAT-WIDGET'])

const short = (value: string, max: number) => {
    const clean = value.replace(/\s+/g, ' ').trim()
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean
}

/**
 * The element a gesture on `target` stands for: the nearest the snapshot
 * describes on its own, from the innermost node up, across open shadow
 * roots. Undefined on the widget, on what the agent's rules exclude, and on
 * the page as a whole.
 */
export function pointedFrom(
    target: Element,
    rules: PageRules,
): Element | undefined {
    if (isExcluded(target, rules)) {
        return undefined
    }
    for (const element of composedAncestors(target)) {
        if (element.matches(TOO_WIDE)) {
            break
        }
        if (
            element.matches(POINTABLE) &&
            !NO_ROLE.has(element.getAttribute('role') ?? '')
        ) {
            return isHidden(element, { inert: false }) ? undefined : element
        }
    }
    // A bare box with text of its own, as many sites write their cards
    return !target.matches(TOO_WIDE) &&
        filteredText(target, rules, SELECTED_MIN_CHARS).length > 0
        ? target
        : undefined
}

/**
 * The page's element at a point of the window, under the widget's own layers
 * (the layer that takes the pointer while the user points), inside open
 * shadow roots.
 */
export function elementAt(x: number, y: number): Element | undefined {
    const found = document
        .elementsFromPoint(x, y)
        .find((element) => !WIDGET_TAGS.has(element.tagName))
    let element = found
    while (element?.shadowRoot) {
        const inner = element.shadowRoot.elementFromPoint(x, y)
        if (!inner || inner === element) {
            break
        }
        element = inner
    }
    return element
}

/** The name the chip and the model give the element: its own, or its text. */
const nameFor = (element: Element, rules: PageRules) =>
    nameOf(element, rules) ||
    filteredText(element, rules, LABEL_MAX_CHARS * 2) ||
    roleOf(element) ||
    ''

/** What the box and the chip call an element: its name, or its text, short. */
export const pointerLabel = (element: Element, rules: PageRules): string =>
    short(nameFor(element, rules), LABEL_MAX_CHARS)

/**
 * The part of the page around `element` that still reads short: the largest
 * of its ancestors whose text fits, so the model sees the form or the card it
 * belongs to, and never the whole page.
 */
const regionAround = (element: Element, rules: PageRules): Element => {
    let region = element
    for (const ancestor of composedAncestors(element)) {
        if (ancestor === element) {
            continue
        }
        if (
            ancestor.matches(TOO_WIDE) ||
            filteredText(ancestor, rules, AROUND_MAX_CHARS + 1).length >
                AROUND_MAX_CHARS
        ) {
            break
        }
        region = ancestor
    }
    return region
}

/**
 * What the message carries of a pointed element: its line and the part
 * around it, read as `page_snapshot` reads them with the same refs, so the
 * model can act on it at once. The rules apply as in any snapshot: an
 * excluded part or a sensitive value never shows.
 */
export function describePointed(
    element: Element,
    refs: PageRefs,
    rules: PageRules,
    selected?: string,
): PagePointer {
    const ref = refs.refOf(element)
    const name = nameFor(element, rules)
    const line = roleOf(element)
        ? describe(element, ref, rules)
        : `[${ref}] text ${JSON.stringify(short(name, 300))}`
    const context = snapshot(regionAround(element, rules), refs, rules, {
        maxChars: AROUND_MAX_CHARS,
    }).text
    const text = selected ? short(selected, SELECTED_MAX_CHARS) : undefined
    return {
        kind: text ? 'text' : 'element',
        label: short(text ?? name, LABEL_MAX_CHARS),
        line,
        context,
        ...(text && { text }),
        // The path only, as page_snapshot gives it: a query may hold a token
        page: {
            title: short(document.title, 200),
            path: location.pathname.slice(0, 500),
        },
    }
}

/** A text the user selected on the page, where it ends on screen, and the element it is in. */
export type PageSelection = {
    text: string
    element: Element
    rect: { left: number; top: number; right: number; bottom: number }
}

/**
 * The text selected on the page now, when it may go to the agent: not in the
 * widget, not touching a part the rules exclude (a sensitive field, an
 * excluded selector, the widget), long enough to ask about.
 */
export function selectionOnPage(rules: PageRules): PageSelection | undefined {
    const selection = document.getSelection()
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return undefined
    }
    // The first range only, the one checked below: a browser that keeps
    // several would carry the others unchecked
    const range = selection.getRangeAt(0)
    const text = short(range.toString(), SELECTED_MAX_CHARS)
    if (text.length < SELECTED_MIN_CHARS) {
        return undefined
    }
    const common = range.commonAncestorContainer
    const container =
        common instanceof Element ? common : (common.parentElement ?? null)
    if (!container || isExcluded(container, rules)) {
        return undefined
    }
    // An excluded part inside the selection would go with its text: only the
    // elements an exclusion can match are looked at
    const candidates = container.querySelectorAll(
        [
            '[data-agent-ignore]',
            'input',
            'label',
            'embedded-chat-widget',
            'floating-chat-widget',
            ...rules.excludeSelectors,
        ].join(', '),
    )
    for (const candidate of candidates) {
        if (range.intersectsNode(candidate) && isExcluded(candidate, rules)) {
            return undefined
        }
    }
    const rects = range.getClientRects()
    const last = rects[rects.length - 1] ?? range.getBoundingClientRect()
    return {
        text,
        element: pointedFrom(container, rules) ?? container,
        rect: {
            left: last.left,
            top: last.top,
            right: last.right,
            bottom: last.bottom,
        },
    }
}
